/* Pure geometry shared by the browser and Node tests. No DOM or font guesses.
 * Rows accept id, rendered metrics, optional normalized flower geometry,
 * nameScale/flowerScale (default 1), and editedComponent ('name' or 'flower').
 * Scales are multipliers of a shared automatic starting composition, never
 * inputs to its group sizing. Store returned effective scales after edits.
 */
(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.BloomBalance = api;
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const DEFAULTS = Object.freeze({
    width: 300, height: 620, padding: 26, maxFontSize: 118,
    flowerRatio: 2.4, minimumFlowerFactor: 0.70, matchNames: true,
    flower: Object.freeze({
      anchorX: 738.5 / 1688, anchorY: 1687.5 / 1688,
      xMin: 376 / 1688, yMin: 0, xMax: 1312 / 1688, yMax: 1
    })
  });
  function parseNames(value) {
    return String(value).split(/\r?\n/).map(name =>
      name.trim().replace(/[~\uE000]+$/g, '').trim().normalize('NFC')
    ).filter(Boolean);
  }
  function rect(xMin, yMin, xMax, yMax) {
    return { xMin, yMin, xMax, yMax, width: xMax - xMin, height: yMax - yMin };
  }
  function union(a, b) {
    return rect(Math.min(a.xMin, b.xMin), Math.min(a.yMin, b.yMin),
      Math.max(a.xMax, b.xMax), Math.max(a.yMax, b.yMax));
  }
  function geometry(row, fontSize, flowerSize, flower = row.flower || DEFAULTS.flower) {
    const scale = fontSize / row.unitsPerEm;
    const endpoint = {
      x: row.advance - row.stem.advance + row.stem.endpoint.x,
      y: row.stem.endpoint.y
    };
    // SVG text has downward y. After rotate(-90), its endpoint is the origin.
    const ink = row.ink;
    const text = rect((ink.y + endpoint.y) * scale,
      (endpoint.x - ink.x - ink.width) * scale,
      (ink.y + ink.height + endpoint.y) * scale,
      (endpoint.x - ink.x) * scale);
    const blossom = rect((flower.xMin - flower.anchorX) * flowerSize,
      (flower.yMin - flower.anchorY) * flowerSize,
      (flower.xMax - flower.anchorX) * flowerSize,
      (flower.yMax - flower.anchorY) * flowerSize);
    return { endpoint, text, flower: blossom, bounds: union(text, blossom) };
  }
  function fits(bounds, options) {
    return bounds.width <= options.width - 2 * options.padding + 1e-7 &&
      bounds.height <= options.height - 2 * options.padding + 1e-7;
  }
  function validate(row) {
    const values = [row.unitsPerEm, row.advance, row.ink.x, row.ink.y,
      row.ink.width, row.ink.height, row.stem.advance,
      row.stem.endpoint.x, row.stem.endpoint.y];
    if (!values.every(Number.isFinite) || row.unitsPerEm <= 0 ||
        row.ink.width < 0 || row.ink.height < 0) {
      throw new TypeError('A name needs finite, rendered glyph metrics.');
    }
    for (const key of ['nameScale', 'flowerScale']) {
      if (row[key] !== undefined && (!Number.isFinite(row[key]) || row[key] <= 0)) {
        throw new RangeError('Individual sizes must be finite positive scales.');
      }
    }
    if (row.editedComponent !== undefined && !['name', 'flower'].includes(row.editedComponent)) {
      throw new TypeError('editedComponent must be name or flower.');
    }
  }
  function validateFlower(flower) {
    if (!['anchorX', 'anchorY', 'xMin', 'yMin', 'xMax', 'yMax'].every(key => Number.isFinite(flower[key])) ||
        flower.xMax <= flower.xMin || flower.yMax <= flower.yMin) {
      throw new TypeError('A flower needs finite normalized alpha bounds and an anchor.');
    }
  }
  const MIN_SCALE = 1e-6;
  const MAX_SCALE = 1e6;
  const SCALE_EPSILON = 1e-7;
  function maximumScale(moving, fixed, options) {
    // For every axis, max(moving.max*s, fixed.max) -
    // min(moving.min*s, fixed.min) <= available. Each of the four
    // pairwise differences yields a linear bound. Including the join in
    // both boxes makes these limits monotonic even for off-ink anchors.
    let maximum = MAX_SCALE;
    for (const [axis, available] of [['x', options.width - 2 * options.padding], ['y', options.height - 2 * options.padding]]) {
      const low = Math.min(0, moving[axis + 'Min']);
      const high = Math.max(0, moving[axis + 'Max']);
      const fixedLow = Math.min(0, fixed[axis + 'Min']);
      const fixedHigh = Math.max(0, fixed[axis + 'Max']);
      if (high > low) maximum = Math.min(maximum, available / (high - low));
      if (high > 0) maximum = Math.min(maximum, (available + fixedLow) / high);
      if (low < 0) maximum = Math.min(maximum, (available - fixedHigh) / -low);
    }
    return Math.max(MIN_SCALE, maximum);
  }
  function componentLimits(row, baseFontSize, baseFlowerSize, nameScale, flowerScale, flower, options) {
    const base = geometry(row, baseFontSize, baseFlowerSize, flower);
    const current = geometry(row, baseFontSize * nameScale, baseFlowerSize * flowerScale, flower);
    return {
      name: maximumScale(base.text, current.flower, options),
      flower: maximumScale(base.flower, current.text, options)
    };
  }
  function solveBalance(rows, settings = {}) {
    const options = { ...DEFAULTS, ...settings, flower: { ...DEFAULTS.flower, ...settings.flower } };
    if (![options.width, options.height, options.padding, options.maxFontSize,
      options.flowerRatio, options.minimumFlowerFactor].every(Number.isFinite) ||
      options.padding < 0 || options.width <= options.padding * 2 ||
      options.height <= options.padding * 2 || options.maxFontSize <= 0 ||
      options.flowerRatio <= 0 || options.minimumFlowerFactor <= 0 || options.minimumFlowerFactor > 1) {
      throw new RangeError('Invalid proof dimensions or flower balance.');
    }
    rows.forEach(validate);
    const flowers = rows.map(row => ({ ...options.flower, ...row.flower }));
    flowers.forEach(validateFlower);
    if (!rows.length) return [];
    // At the flower floor both geometries scale linearly with text size.
    // This computes the largest text size that can fit after reducing the flower.
    const limits = rows.map((row, index) => {
      const minimum = options.maxFontSize * options.flowerRatio * options.minimumFlowerFactor;
      const b = geometry(row, options.maxFontSize, minimum, flowers[index]).bounds;
      return options.maxFontSize * Math.min(1,
        (options.width - 2 * options.padding) / Math.max(b.width, 1e-12),
        (options.height - 2 * options.padding) / Math.max(b.height, 1e-12));
    });
    const commonSize = Math.min(...limits);
    return rows.map((row, index) => {
      // The automatic starting composition ignores ALL row size overrides.
      // Editing one row therefore never changes a neighboring row's baseline.
      const flower = flowers[index];
      const baseFontSize = options.matchNames ? commonSize : limits[index];
      const desired = baseFontSize * options.flowerRatio;
      let low = desired * options.minimumFlowerFactor;
      let high = desired;
      if (fits(geometry(row, baseFontSize, high, flower).bounds, options)) low = high;
      else {
        // Union extents are monotonic because both component bounds contain
        // the join. Fixed iterations make results deterministic across runs.
        for (let i = 0; i < 42; i++) {
          const middle = (low + high) / 2;
          if (fits(geometry(row, baseFontSize, middle, flower).bounds, options)) low = middle;
          else high = middle;
        }
      }
      const baseFlowerSize = low;
      const requestedNameScale = row.nameScale ?? 1;
      const requestedFlowerScale = row.flowerScale ?? 1;
      let effectiveNameScale = requestedNameScale;
      let effectiveFlowerScale = requestedFlowerScale;
      const editedComponent = row.editedComponent || 'flower';
      // Preserve the component NOT being edited, then clamp only the edited
      // component to the remaining room. Its partner is clamped only if a
      // changed font/flower makes that partner impossible even on its own.
      if (editedComponent === 'name') {
        const capacity = componentLimits(row, baseFontSize, baseFlowerSize, MIN_SCALE, 1, flower, options);
        effectiveFlowerScale = Math.min(requestedFlowerScale, capacity.flower);
        const remaining = componentLimits(row, baseFontSize, baseFlowerSize, 1, effectiveFlowerScale, flower, options);
        effectiveNameScale = Math.min(requestedNameScale, remaining.name);
      } else {
        const capacity = componentLimits(row, baseFontSize, baseFlowerSize, 1, MIN_SCALE, flower, options);
        effectiveNameScale = Math.min(requestedNameScale, capacity.name);
        const remaining = componentLimits(row, baseFontSize, baseFlowerSize, effectiveNameScale, 1, flower, options);
        effectiveFlowerScale = Math.min(requestedFlowerScale, remaining.flower);
      }
      const fontSize = baseFontSize * effectiveNameScale;
      const flowerSize = baseFlowerSize * effectiveFlowerScale;
      const maximum = componentLimits(row, baseFontSize, baseFlowerSize, effectiveNameScale, effectiveFlowerScale, flower, options);
      const clamped = {
        name: effectiveNameScale + SCALE_EPSILON < requestedNameScale,
        flower: effectiveFlowerScale + SCALE_EPSILON < requestedFlowerScale
      };
      const g = geometry(row, fontSize, flowerSize, flower);
      const joinX = options.width / 2 - (g.bounds.xMin + g.bounds.xMax) / 2;
      const joinY = options.height / 2 - (g.bounds.yMin + g.bounds.yMax) / 2;
      const scale = fontSize / row.unitsPerEm;
      return {
        id: row.id ?? String(index), name: row.name,
        baseFontSize, baseFlowerSize, baseFlowerFactor: baseFlowerSize / desired,
        requestedNameScale, requestedFlowerScale, effectiveNameScale, effectiveFlowerScale,
        maxNameScale: maximum.name, maxFlowerScale: maximum.flower,
        atLimit: clamped.name || clamped.flower, clamped,
        nameAtLimit: effectiveNameScale >= maximum.name - SCALE_EPSILON,
        flowerAtLimit: effectiveFlowerScale >= maximum.flower - SCALE_EPSILON,
        fontSize, flowerSize, flowerFactor: flowerSize / desired,
        stemWidth: row.stem.tipWidth * scale,
        stemJoinText: row.stem.joinProfile ? { ...row.stem.joinProfile,
          distance: row.stem.joinProfile.distance * scale,
          left: row.stem.joinProfile.left * scale, right: row.stem.joinProfile.right * scale } : null,
        nameFactor: fontSize / options.maxFontSize,
        flowerGeometry: flower,
        join: { x: joinX, y: joinY }, endpoint: g.endpoint,
        textTransform: { x: joinX + g.endpoint.y * scale, y: joinY + g.endpoint.x * scale, rotation: -90 },
        flowerPosition: { x: joinX - flower.anchorX * flowerSize, y: joinY - flower.anchorY * flowerSize },
        bounds: rect(joinX + g.bounds.xMin, joinY + g.bounds.yMin,
          joinX + g.bounds.xMax, joinY + g.bounds.yMax),
        fits: fits(g.bounds, options)
      };
    });
  }
  return Object.freeze({ DEFAULTS, parseNames, geometry, solveBalance });
});
